import { useEffect, useRef } from 'react';
import { useStore } from '@/state/store';
import { SimulationClient } from '@/workers/client';

/**
 * Drives the simulation worker.
 *
 * One step request is in flight at a time; the render loop in FoldedViewer is
 * completely independent of it. When "play" is on, the fold amount is advanced
 * here rather than in a React effect chain so that pausing is instant.
 */
export function useSimulation(): void {
  const clientRef = useRef<SimulationClient | null>(null);
  const rafRef = useRef(0);
  const lastTime = useRef(performance.now());

  useEffect(() => {
    const client = new SimulationClient();
    clientRef.current = client;
    client.onFrame((frame) => {
      useStore.getState().setFrame(frame);
      const seen = new Set<string>();
      for (const w of frame.warnings) {
        if (seen.has(w)) continue;
        seen.add(w);
        useStore.getState().notify('warning', w);
      }
    });
    client.onError((message) => {
      useStore.getState().notify('error', `Simulation worker: ${message}`);
    });
    client.onLoaded((warnings) => {
      for (const w of warnings) useStore.getState().notify('warning', w);
    });
    client.load(useStore.getState().pattern);

    const unsubscribe = useStore.subscribe((state, previous) => {
      if (state.pattern !== previous.pattern) {
        client.load(state.pattern);
      }
    });

    const loop = (): void => {
      const now = performance.now();
      const dt = Math.min((now - lastTime.current) / 1000, 0.1);
      lastTime.current = now;

      const state = useStore.getState();
      if (state.sim.playing) {
        const next = state.sim.foldAmount + state.sim.playDirection * state.sim.speed * dt;
        if (next >= 1) {
          state.setFoldAmount(1);
          useStore.setState((s) => ({ sim: { ...s.sim, playDirection: -1 } }));
        } else if (next <= 0 && state.sim.playDirection === -1) {
          state.setFoldAmount(0);
          useStore.setState((s) => ({ sim: { ...s.sim, playDirection: 1 } }));
        } else {
          state.setFoldAmount(next);
        }
      }
      client.requestStep(useStore.getState().engineParams(), 1);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      unsubscribe();
      client.dispose();
      clientRef.current = null;
    };
  }, []);
}
